import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CommonService } from '../../../shared/services/common.service';
import { API_ENDPOINTS } from '../../../core/services/api.service';
import { environment } from '../../../../environments/environment';
import { HttpHeaders } from '@angular/common/http';

/**
 * Service to handle master management operations including roles, templates, and environments
 */
@Injectable({
  providedIn: 'root'
})

export class MasterManagementService {
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly customerApiBaseUrl = environment.customerApiBaseUrl;
  private readonly endpoints = API_ENDPOINTS.MASTER_MANAGEMENT;

  constructor(private commonService: CommonService) { }

  getRolesByCustomerId(customer_id: any): Observable<any> {
    const headers = new HttpHeaders({ 'x-customer-id': customer_id });
    return this.commonService.httpSvc.get(
      environment.apiBaseUrl + this.endpoints.ROLES.GET_BY_CUSTOMER_ID,
      headers
    );
  }

  createRole(roleData: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.ROLES.ADD}`,
      roleData
    );
  }

  deleteRole(roleId: number): Observable<any> {
    const payload = { "role_id": roleId };
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.ROLES.DELETE}`,
      payload
    );
  }

  updateRole(roleId: number, customerId: any, roleData: any): Observable<any> {
    const headers = new HttpHeaders({ 'x-customer-id': customerId });

    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.ROLES.UPDATE}`,
      roleId,
      roleData,
      headers
    );
  }

  deleteTemplate(payload: object): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.TEMPLATES.DELETE}`,
      payload
    );
  }

  getEnvironments(): Observable<any> {
    return this.commonService.httpSvc.get(
      `${this.apiBaseUrl}${this.endpoints.ENVIRONMENTS.GET_BY_CUSTOMER_ID}`
    );
  }

  getEnvironmentsByCustomerId(payload: any, applicationId?: number): Observable<any> {
    const url = `${this.apiBaseUrl}${this.endpoints.ENVIRONMENTS.GET_BY_CUSTOMER_ID}/${payload.customer_id}`;
    if (applicationId != null) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'x-app-id': String(applicationId),
      });
      return this.commonService.httpSvc.get(url, headers);
    }
    return this.commonService.httpSvc.get(url);
  }

  addEnvironment(payload: any, applicationId?: number): Observable<any> {
    const url = `${this.apiBaseUrl}${this.endpoints.ENVIRONMENTS.ADD}`;
    if (applicationId != null) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'x-app-id': String(applicationId),
      });
      return this.commonService.httpSvc.post(url, payload, headers);
    }
    return this.commonService.httpSvc.post(url, payload);
  }

  deleteEnvironment(id: number): Observable<any> {
    return this.commonService.httpSvc.delete(
      `${this.apiBaseUrl}${this.endpoints.ENVIRONMENTS.DELETE}`, id
    );
  }

  updateEnvironment(id: number, payload: any): Observable<any> {
    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.ENVIRONMENTS.UPDATE}`, id, payload
    );
  }

  getExcursionsByCustomerId(payload: any): Observable<any> {
    return this.commonService.httpSvc.getById(
      `${this.apiBaseUrl}${this.endpoints.EXCURSIONS.GET_BY_CUSTOMER_ID}`,
      payload.customer_id
    );
  }

  addExcursion(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.EXCURSIONS.ADD}`,
      payload
    );
  }

  deleteExcursion(id: number): Observable<any> {
    return this.commonService.httpSvc.delete(
      `${this.apiBaseUrl}${this.endpoints.EXCURSIONS.DELETE}`, id
    );
  }

  updateExcursion(id: number, payload: any): Observable<any> {
    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.EXCURSIONS.UPDATE}`, id, payload
    );
  }

  getCapabilities(): Observable<any> {
    return this.commonService.httpSvc.get(
      `${this.apiBaseUrl}${this.endpoints.CAPABILITIES.GET}`
    );
  }

  addCapability(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.CAPABILITIES.ADD}`,
      payload
    );
  }

  deleteCapability(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.CAPABILITIES.DELETE}`,
      payload
    );
  }

  updateCapability(id: number, payload: any): Observable<any> {
    // Backend accepts /capabilities/:id with PATCH body as changed fields
    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.CAPABILITIES.UPDATE}`,
      id,
      payload
    );
  }

  getCommunicationTemplates(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.COMMUNICATION.GET_ALL_TEMPLATES}`,
      payload
    );
  }

  createCommunicationTemplate(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.COMMUNICATION.CREATE}`,
      payload
    );
  }

  updateCommunicationTemplate(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.COMMUNICATION.UPDATE}`,
      payload
    );
  }

  getAbbreviationsByCustomerId(customerId: number | string): Observable<any> {
    return this.commonService.httpSvc.getById(
      `${this.apiBaseUrl}${this.endpoints.ABBREVIATIONS.GET_BY_CUSTOMER_ID}`,
      customerId
    );
  }

  addAbbreviation(payload: any): Observable<any> {
    return this.commonService.httpSvc.post(
      `${this.apiBaseUrl}${this.endpoints.ABBREVIATIONS.ADD}`,
      payload
    );
  }

  updateAbbreviation(id: number, payload: any): Observable<any> {
    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.ABBREVIATIONS.UPDATE}`,
      id,
      payload
    );
  }

  deleteAbbreviation(id: number, payload: any): Observable<any> {
    return this.commonService.httpSvc.patch(
      `${this.apiBaseUrl}${this.endpoints.ABBREVIATIONS.DELETE}`,
      id,
      payload
    );
  }

  validateAbbreviation(customerId: number | string, abbreviation: string, description: string): Observable<any> {
    return this.commonService.httpSvc.get(
      `${this.apiBaseUrl}${this.endpoints.ABBREVIATIONS.VALIDATE}?customer_id=${customerId}&abbreviation=${encodeURIComponent(abbreviation)}&description=${encodeURIComponent(description)}`
    );
  }
}
